// FP3-035 — Assessment Runner card. Sits next to the existing
// "Assessment artifacts" upload card on the Student Workspace
// Assessments tab. Shows completed/draft runs in a single timeline
// and exposes the Start assessment button.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= on assessment-run navigation.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { toast } from "sonner";
import {
  ClipboardText,
  Plus,
  PencilSimple,
  Eye,
  TrashSimple,
  Star,
  Timer,
  Sparkle,
} from "@phosphor-icons/react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import AssessmentRunnerDialog from "./AssessmentRunnerDialog";
import AssessmentRunViewerDialog from "./AssessmentRunViewerDialog";

const TYPE_LABEL = {
  skill_probe: { label: "Skill Probe", icon: ClipboardText, tone: "bg-[#E8F0EB] text-[#1E3F33]" },
  rubric_rating: { label: "Rubric", icon: Star, tone: "bg-[#FDF1E1] text-[#9C6B2E]" },
  timed_trial: { label: "Timed Trial", icon: Timer, tone: "bg-[#E8EFF7] text-[#1E5A8F]" },
};

function fmtScore(run) {
  const s = run.summary || {};
  if (run.assessment_type === "rubric_rating") {
    if (s.average_rating == null) return "—";
    return `${s.average_rating}/5 avg`;
  }
  if (s.percent_accuracy == null) return "—";
  let txt = `${s.percent_accuracy}%`;
  if (s.correct_count != null && s.total_count) {
    txt += ` · ${s.correct_count}/${s.total_count}`;
  }
  if (run.assessment_type === "timed_trial" && s.trials_per_minute != null) {
    txt += ` · ${s.trials_per_minute} tpm`;
  }
  return txt;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AssessmentRunsCard({ student, canEdit = true }) {
  const studentId = student?.id;
  const navigate = useShadowNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runnerOpen, setRunnerOpen] = useState(false);
  const [resumeRun, setResumeRun] = useState(null);
  const [viewerRun, setViewerRun] = useState(null);
  const [pendingArchive, setPendingArchive] = useState(null);

  const refresh = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const r = await api.listAssessmentRuns(studentId);
      setRuns(r.data?.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const drafts = useMemo(() => runs.filter((r) => r.status === "draft"), [runs]);
  const completed = useMemo(() => runs.filter((r) => r.status === "completed"), [runs]);

  const handleSaved = (saved) => {
    setRunnerOpen(false);
    setResumeRun(null);
    refresh();
    if (saved?.status === "completed") {
      toast.success(`Assessment complete · ${fmtScore(saved)}`);
    }
  };

  const handleArchive = async (run) => {
    try {
      await api.archiveAssessmentRun(studentId, run.id);
      toast.success("Assessment archived");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to archive");
    } finally {
      setPendingArchive(null);
    }
  };

  if (!studentId) return null;

  return (
    <Card
      data-testid="assessment-runs-card"
      className="border-[#E5E3DB] bg-white"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-[15px] heading-font flex items-center gap-2 text-[#1F2937]">
            <ClipboardText size={16} weight="duotone" className="text-[#9C6B2E]" />
            Assessment administrations
          </CardTitle>
          <p className="text-xs text-[#6B7280]">
            Provider-administered informal probes, rubrics, and timed trials.
          </p>
        </div>
        {canEdit && (
          <Button
            size="sm"
            data-testid="assessment-runs-start-btn"
            onClick={() => {
              setResumeRun(null);
              setRunnerOpen(true);
            }}
            className="h-8 text-xs bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Plus size={13} className="mr-1" /> Start assessment
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {loading ? (
          <p className="text-xs text-[#6B7280]">Loading…</p>
        ) : (
          <>
            {drafts.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#9C6B2E]">
                  Drafts
                </p>
                {drafts.map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    canEdit={canEdit}
                    onView={() => setViewerRun(r)}
                    onResume={() => {
                      setResumeRun(r);
                      setRunnerOpen(true);
                    }}
                    onArchive={() => setPendingArchive(r)}
                  />
                ))}
              </div>
            )}
            {completed.length === 0 && drafts.length === 0 && (
              <div
                data-testid="assessment-runs-empty"
                className="text-xs text-[#6B7280] italic py-3 text-center"
              >
                No assessment administrations yet. Click <strong>Start assessment</strong> to run a probe.
              </div>
            )}
            {completed.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#1E3F33]">
                  Completed
                </p>
                {completed.map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    canEdit={canEdit}
                    onView={() => setViewerRun(r)}
                    onArchive={() => setPendingArchive(r)}
                    onUseAsEvidence={() => {
                      // FP3-035 correction — Direct affordance from
                      // the Assessments tab into the Goal Plan AI
                      // modal with this run pre-selected. Uses a
                      // URL query so a hard-refresh of the Goal Plan
                      // tab still arrives in the right state.
                      navigate(
                        `/students/${studentId}?tab=goal-plan&suggest-run=${r.id}`,
                      );
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <AssessmentRunnerDialog
        open={runnerOpen}
        onOpenChange={(v) => {
          setRunnerOpen(v);
          if (!v) setResumeRun(null);
        }}
        student={student}
        existingRun={resumeRun}
        onSaved={handleSaved}
      />

      <AssessmentRunViewerDialog
        run={viewerRun}
        student={student}
        onClose={() => setViewerRun(null)}
        onRunUpdated={(updated) => {
          // FP3-035C — AI interpretation generation returns the
          // refreshed run document. Refresh the list + dialog state.
          setViewerRun(updated);
          handleSaved();
        }}
      />

      <AlertDialog
        open={!!pendingArchive}
        onOpenChange={(v) => !v && setPendingArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingArchive?.title} will be hidden from this list. Past Goal
              Plan suggestions that referenced it will still resolve, but new
              suggestions won't include it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assessment-runs-archive-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="assessment-runs-archive-confirm"
              onClick={() => pendingArchive && handleArchive(pendingArchive)}
              className="bg-[#7A3A2C] hover:bg-[#5C2A1F] text-white"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function RunRow({ run, canEdit, onView, onResume, onArchive, onUseAsEvidence }) {
  const meta = TYPE_LABEL[run.assessment_type] || TYPE_LABEL.skill_probe;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`assessment-run-row-${run.id}`}
      className="flex items-center gap-3 px-2 py-1.5 rounded-lg border border-[#F1F2F4] hover:border-[#E5E3DB] hover:bg-[#FBFAF6]"
    >
      <Badge className={`text-[10px] gap-1 ${meta.tone}`}>
        <Icon size={10} weight="fill" /> {meta.label}
      </Badge>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-[#1F2937] truncate">
          {run.title || "(untitled)"}
        </p>
        <p className="text-[11px] text-[#6B7280] truncate">
          {fmtDate(run.date_administered)}
          {run.skill_area ? ` · ${run.skill_area}` : ""}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p
          data-testid={`assessment-run-score-${run.id}`}
          className="text-[12px] font-semibold tabular-nums text-[#1E3F33]"
        >
          {fmtScore(run)}
        </p>
        <p className="text-[10px] text-[#6B7280]">{run.summary?.scored_count ?? 0} scored</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {run.status === "draft" && canEdit && (
          <Button
            size="sm"
            variant="ghost"
            data-testid={`assessment-run-resume-${run.id}`}
            onClick={onResume}
            className="h-7 px-2 text-[11px] text-[#1E3F33]"
          >
            <PencilSimple size={12} className="mr-1" /> Resume
          </Button>
        )}
        {run.status === "completed" && onUseAsEvidence && (
          <Button
            size="sm"
            variant="ghost"
            data-testid={`assessment-run-use-as-evidence-${run.id}`}
            onClick={onUseAsEvidence}
            title="Open Goal Plan and pre-select this run as AI evidence"
            className="h-7 px-2 text-[11px] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            <Sparkle size={12} weight="duotone" className="mr-1" />
            Use as evidence
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          data-testid={`assessment-run-view-${run.id}`}
          onClick={onView}
          className="h-7 px-2 text-[11px] text-[#6B7280]"
        >
          <Eye size={12} className="mr-1" /> View
        </Button>
        {canEdit && (
          <Button
            size="sm"
            variant="ghost"
            data-testid={`assessment-run-archive-${run.id}`}
            onClick={onArchive}
            className="h-7 px-2 text-[11px] text-[#7A3A2C] hover:bg-[#F8E8E2]"
          >
            <TrashSimple size={12} />
          </Button>
        )}
      </div>
    </div>
  );
}
